import { z } from 'zod';

import {
  employeeStatusValues,
  employmentTypeValues,
  type CreateEmployeeRequest,
  type CreateEmployeeResponse,
  type EmployeeDto,
  type EmployeeStatus,
  type EmploymentType,
  type ListEmployeesRequest,
  type ListEmployeesResponse,
  type UpdateEmployeeRequest,
  type UpdateEmployeeResponse,
} from './employees.dto.js';

const timestampSchema = z.iso.datetime();
const dateSchema = z.iso.date();
const employeeNumberSchema = z.string().trim().min(1).max(50);
const timeZoneSchema = z.string().trim().min(1).refine(isValidIanaTimeZone, { message: 'Invalid time zone' });

function isValidIanaTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

export const EmployeeStatusSchema: z.ZodType<EmployeeStatus> = z.enum(employeeStatusValues);
export const EmploymentTypeSchema: z.ZodType<EmploymentType> = z.enum(employmentTypeValues);

export const EmployeeDtoSchema: z.ZodType<EmployeeDto> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid().nullable(),
  userId: z.uuid().nullable(),
  employeeNumber: employeeNumberSchema.nullable(),
  timezone: timeZoneSchema.optional(),
  firstName: z.string().trim().min(1),
  lastName: z.string().trim().min(1),
  displayName: z.string().trim().min(1),
  status: EmployeeStatusSchema,
  hireDate: dateSchema.nullable(),
  terminationDate: dateSchema.nullable(),
  employmentType: EmploymentTypeSchema.nullable(),
  assignments: z.array(
    z.object({
      companyId: z.uuid(),
      employeeId: z.uuid(),
      storeId: z.uuid(),
      locationId: z.uuid().nullable(),
      isPrimary: z.boolean(),
      createdAt: timestampSchema,
      updatedAt: timestampSchema,
    }),
  ).optional(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const CreateEmployeeRequestSchema: z.ZodType<CreateEmployeeRequest> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid().nullable().optional(),
  timezone: timeZoneSchema.optional(),
  firstName: z.string().trim().min(1),
  lastName: z.string().trim().min(1),
  displayName: z.string().trim().min(1).optional(),
  employeeNumber: employeeNumberSchema.nullable().optional(),
  userId: z.uuid().nullable().optional(),
  hireDate: dateSchema.nullable().optional(),
  terminationDate: dateSchema.nullable().optional(),
  employmentType: EmploymentTypeSchema.nullable().optional(),
});

export const CreateEmployeeResponseSchema: z.ZodType<CreateEmployeeResponse> = z.object({
  employee: EmployeeDtoSchema,
});

export const UpdateEmployeeRequestSchema: z.ZodType<UpdateEmployeeRequest> = z.object({
  companyId: z.uuid(),
  employeeId: z.uuid(),
  firstName: z.string().trim().min(1).optional(),
  lastName: z.string().trim().min(1).optional(),
  displayName: z.string().trim().min(1).optional(),
  status: EmployeeStatusSchema.optional(),
  storeId: z.uuid().optional(),
  locationId: z.uuid().nullable().optional(),
  employeeNumber: employeeNumberSchema.nullable().optional(),
  userId: z.uuid().nullable().optional(),
  timezone: timeZoneSchema.nullable().optional(),
  hireDate: dateSchema.nullable().optional(),
  terminationDate: dateSchema.nullable().optional(),
  employmentType: EmploymentTypeSchema.nullable().optional(),
});

export const UpdateEmployeeResponseSchema: z.ZodType<UpdateEmployeeResponse> = z.object({
  employee: EmployeeDtoSchema,
});

export const ListEmployeesRequestSchema: z.ZodType<ListEmployeesRequest> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid().optional(),
  locationId: z.uuid().optional(),
  status: EmployeeStatusSchema.optional(),
});

export const ListEmployeesResponseSchema: z.ZodType<ListEmployeesResponse> = z.object({
  items: z.array(EmployeeDtoSchema),
});
